import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCreateLeadList, useImportLeadRows } from "@/hooks/use-lead-list-actions";
import { useSegmentSearchCount, useSegmentSearchImport } from "@/hooks/use-lead-segment-search";
import type { LeadCsvResult } from "@/lib/lead-csv";
import { EMPTY_SEGMENT_FORM, buildSegment, type SegmentFormState } from "@/lib/lead-segment";
import CsvListFields from "./CsvListFields";
import SegmentFields from "./SegmentFields";

type Source = "csv" | "brreg_search";

interface LeadListFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Ny leadliste (admin): fra CSV eller fra Brønnøysund-søk på bransje, sted, ansatte og omsetning. */
export default function LeadListFormDialog({ open, onOpenChange }: LeadListFormDialogProps) {
  const [source, setSource] = useState<Source>("csv");
  const [name, setName] = useState("");
  const [priority, setPriority] = useState("100");
  const [csv, setCsv] = useState<LeadCsvResult | null>(null);
  const [segmentForm, setSegmentForm] = useState<SegmentFormState>(EMPTY_SEGMENT_FORM);
  const [count, setCount] = useState<number | null>(null);

  const createList = useCreateLeadList();
  const importRows = useImportLeadRows();
  const searchCount = useSegmentSearchCount();
  const searchImport = useSegmentSearchImport();

  useEffect(() => {
    if (!open) return;
    setSource("csv");
    setName("");
    setPriority("100");
    setCsv(null);
    setSegmentForm(EMPTY_SEGMENT_FORM);
    setCount(null);
  }, [open]);

  const priorityNumber = Number(priority);
  const priorityValid = Number.isInteger(priorityNumber) && priorityNumber >= 0;
  const busy = createList.isPending || importRows.isPending || searchImport.isPending;
  const ready = name.trim() !== "" && priorityValid && (source === "csv" ? !!csv && csv.rows.length > 0 : true);

  const previewCount = () => {
    const built = buildSegment(segmentForm);
    if ("error" in built) return toast.error(built.error);
    searchCount.mutate(built.segment, { onSuccess: setCount });
  };

  const submit = async () => {
    const built = source === "brreg_search" ? buildSegment(segmentForm) : null;
    if (built && "error" in built) return toast.error(built.error);
    const list = await createList.mutateAsync({
      name,
      source,
      priority: priorityNumber,
      segment: built && "segment" in built ? built.segment : null,
    });
    if (source === "csv" && csv) {
      const res = await importRows.mutateAsync({ listId: list.id, rows: csv.rows });
      toast.success(`${res.inserted} nye, ${res.skipped_customer} allerede kunde, ${res.skipped_duplicate} duplikater`);
    } else if (built && "segment" in built) {
      const res = await searchImport.mutateAsync({ listId: list.id, segment: built.segment });
      toast.success(`${res.inserted ?? 0} nye leads fra ${res.fetched ?? 0} treff${res.truncated ? " (søket ble avkortet)" : ""}`);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ny leadliste</DialogTitle>
          <DialogDescription>Leadene havner i den felles poolen. Møtebookerne ringer dem ett om gangen.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit().catch(() => undefined);
          }}
          className="space-y-5"
        >
          <div className="grid grid-cols-[1fr_8rem] gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lead-list-name">Navn</Label>
              <Input id="lead-list-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-list-priority">Prioritet</Label>
              <Input id="lead-list-priority" inputMode="numeric" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </div>
          </div>
          <p className="-mt-3 text-xs text-muted-foreground">Lavere prioritetstall ringes først.</p>

          <Tabs value={source} onValueChange={(v) => setSource(v as Source)}>
            <TabsList aria-label="Kilde">
              <TabsTrigger value="csv">CSV-fil</TabsTrigger>
              <TabsTrigger value="brreg_search">Brønnøysund-søk</TabsTrigger>
            </TabsList>
          </Tabs>

          {source === "csv" ? (
            <CsvListFields parsed={csv} onParsed={setCsv} />
          ) : (
            <div className="space-y-3">
              <SegmentFields value={segmentForm} onChange={(v) => { setSegmentForm(v); setCount(null); }} />
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={previewCount} disabled={searchCount.isPending}>
                  Forhåndsvis antall
                </Button>
                {count != null && <span className="text-sm">{count} selskaper i Enhetsregisteret</span>}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!ready || busy}>
              {busy ? "Oppretter…" : "Opprett liste"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
